/**
 * Escape markdown-significant characters inside <cite> and <storage-key>
 * bodies so remark leaves them intact for rehypeWrapCite /
 * rehypeWrapStorageKey to parse.
 *
 * - Backslash-quote: double-escaped to survive markdown parsing.
 * - Underscores / asterisks / backticks: would otherwise trigger emphasis or
 *   code formatting inside the body.
 *
 * <storage-key> must stay hyphenated: CommonMark only recognises inline HTML
 * tag names matching [A-Za-z][A-Za-z0-9-]*, so an underscore would make
 * remark pass the tag through as literal text. Its body can be a JSON
 * metadata payload, which the rehype plugin must round-trip as valid JSON.
 */
const escapeBody = (body: string): string =>
  body
    .replace(/\\"/g, '\\\\"')
    .replace(/_/g, "\\_")
    .replace(/\*/g, "\\*")
    .replace(/`/g, "\\`");

export const protectCitationMarkup = (content: string): string => {
  if (!content) return content;
  return content
    .replace(
      /<cite>([^<]*)<\/cite>/g,
      (_, body) => `<cite>${escapeBody(body)}</cite>`,
    )
    .replace(
      /<storage-key([^>]*)>([^<]*)<\/storage-key>/g,
      (_, attrs, body) =>
        `<storage-key${attrs}>${escapeBody(body)}</storage-key>`,
    );
};

const storageKeyText = (attrs: string, body: string): string => {
  // Raw form (<storage-key source="N">TAG</storage-key>) carries the tag id
  // as its body; the resolved form carries a JSON payload.
  if (attrs.trim()) return body;
  try {
    const payload = JSON.parse(body);
    return typeof payload?.storage_key === "string" ? payload.storage_key : "";
  } catch {
    return body;
  }
};

/**
 * Plain-text form of citation markup, for surfaces with no renderer (copy to
 * clipboard). Storage-keys collapse to their tag id; <cite> tags are removed.
 * Mirrors the backend's `strip_citation_tags` used by the exports.
 */
export const stripCitationMarkup = (content: string): string => {
  if (!content) return content;
  return content
    .replace(/<storage-key([^>]*)>([^<]*)<\/storage-key>/g, (_, attrs, body) =>
      storageKeyText(attrs, body),
    )
    .replace(/<cite>[^<]*<\/cite>/g, "");
};
